// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {pushVelocitySample, type VelocitySample} from '@app/features/app/hooks/SwipeVelocity';
import {useContextMenuHoverState} from '@app/features/app/hooks/useContextMenuHoverState';
import {isMediaOnlyEmbed} from '@app/features/channel/components/embeds/EmbedRenderUtils';
import {MessageActionBar} from '@app/features/channel/components/MessageActionBar';
import {MessageActionBottomSheet} from '@app/features/channel/components/MessageActionBottomSheet';
import {
	getMessagePermissions,
	requestDeleteMessage,
	requestMessageReply,
	startMessageEdit,
} from '@app/features/channel/components/MessageActionUtils';
import {useMessageHoverState} from '@app/features/channel/components/MessageHoverState';
import {
	clampSwipeOffset,
	getMessageSwipeRevealStyle,
	type MessageSwipeActionId,
	resolveSwipeAxis,
	SWIPE_MAX_REVEAL_PX,
	shouldCommitSwipe,
	vibrateMessageSwipeCommit,
} from '@app/features/channel/components/MessageSwipeActionModel';
import {
	executeMessageSwipeAction,
	isMessageSwipeActionAvailable,
} from '@app/features/channel/components/MessageSwipeActions';
import {MessageViewContextProvider} from '@app/features/channel/components/MessageViewContext';
import type {Channel} from '@app/features/channel/models/Channel';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import {parse} from '@app/features/messaging/components/markdown/renderers';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {Message as MessageModel} from '@app/features/messaging/models/MessagingMessage';
import MessageEdit from '@app/features/messaging/state/MessageEdit';
import MessageFocus from '@app/features/messaging/state/MessageFocus';
import MessageReply from '@app/features/messaging/state/MessageReply';
import Reminders from '@app/features/messaging/state/Reminders';
import {getMessageComponent} from '@app/features/messaging/utils/MessageComponentUtils';
import {renderAstToPlaintext} from '@app/features/messaging/utils/markdown/Plaintext';
import {NodeType} from '@app/features/messaging/utils/markdown/parser/Enums';
import {SystemMessageUtils} from '@app/features/messaging/utils/SystemMessageUtils';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import ReadStates from '@app/features/read_state/state/ReadStates';
import UnreadCatchUpSettings from '@app/features/read_state/state/UnreadCatchUpSettings';
import {isMarkUnreadChromeTarget, shouldAckMessageOnRowClick} from '@app/features/read_state/utils/UnreadCatchUp';
import styles from '@app/features/theme/styles/Message.module.css';
import {
	AddReactionIcon,
	BookmarkIcon,
	CopyTextIcon,
	DeleteIcon,
	EditIcon,
	ForwardIcon,
	MarkAsUnreadIcon,
	PinIcon,
	ReplyIcon,
	TimeoutIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import {MessageContextMenu} from '@app/features/ui/action_menu/MessageContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {FLUXERBOT_ID} from '@fluxer/constants/src/AppConstants';
import {MessagePreviewContext, MessageStates, MessageTypes} from '@fluxer/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowLeftIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const ATTACHMENT_DESCRIPTOR = msg({
	message: 'attachment',
	comment:
		'Screen-reader fragment listing one attachment on a message. Lowercase because it appears inside a longer sentence.',
});
const ATTACHMENTS_DESCRIPTOR = msg({
	message: '{length, plural, one {# attachment} other {# attachments}}',
	comment: 'Screen-reader fragment listing multiple attachments on a message. length is the count.',
});
const STICKER_DESCRIPTOR = msg({
	message: 'sticker',
	comment:
		'Screen-reader fragment listing one sticker on a message. Lowercase because it appears inside a longer sentence.',
});
const STICKERS_DESCRIPTOR = msg({
	message: '{length, plural, one {# sticker} other {# stickers}}',
	comment: 'Screen-reader fragment listing multiple stickers on a message. length is the count.',
});
const EMBED_DESCRIPTOR = msg({
	message: 'embed',
	comment:
		'Screen-reader fragment listing one embed on a message. Lowercase because it appears inside a longer sentence.',
});
const EMBEDS_DESCRIPTOR = msg({
	message: '{length, plural, one {# embed} other {# embeds}}',
	comment: 'Screen-reader fragment listing multiple embeds on a message. length is the count.',
});
const NO_TEXT_CONTENT_DESCRIPTOR = msg({
	message: 'no text content',
	comment:
		'Screen-reader fragment indicating the message has no text body. Lowercase because it appears inside a longer sentence.',
});
const MESSAGE_DESCRIPTOR = msg({
	message: 'message',
	comment: 'Fallback role label used in screen-reader message summaries.',
});
const MESSAGE_TYPE_NAMES: Record<number, string> = Object.fromEntries(
	Object.entries(MessageTypes).map(([name, value]) => [value, name]),
);
const shouldApplyGroupedLayout = (message: MessageModel, _prevMessage?: MessageModel) => {
	if (message.type !== MessageTypes.DEFAULT && message.type !== MessageTypes.REPLY) {
		return false;
	}
	return true;
};
const isDisplaySystemMessage = (message: MessageModel): boolean =>
	message.type !== MessageTypes.DEFAULT && message.type !== MessageTypes.REPLY;
const isActivationKey = (key: string) => key === 'Enter' || key === ' ' || key === 'Spacebar' || key === 'Space';
const MAX_ARIA_MESSAGE_TEXT_LENGTH = 220;
const LONG_PRESS_DELAY = 500;
const HIGHLIGHT_DELAY = 100;
const SUPPRESS_POST_LONG_PRESS_CLICK_MS = 750;
const NESTED_LONG_PRESS_OWNER_SELECTOR = '[data-long-press-owner="true"]';
const DOUBLE_CLICK_INTERACTIVE_TARGET_SELECTOR =
	'a, button, input, textarea, select, video, audio, [role="button"], [contenteditable="true"]';
const mobileLongPressScrollCancelHandlers = new Set<() => void>();

const renderSwipeActionIcon = (action: MessageSwipeActionId) => {
	switch (action) {
		case 'reply':
			return <ReplyIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.reply-icon" />;
		case 'react':
			return (
				<AddReactionIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.add-reaction-icon" />
			);
		case 'edit':
			return <EditIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.edit-icon" />;
		case 'delete':
			return <DeleteIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.delete-icon" />;
		case 'forward':
			return <ForwardIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.forward-icon" />;
		case 'pin':
			return <PinIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.pin-icon" />;
		case 'bookmark':
			return (
				<BookmarkIcon size={22} filled data-flx="channel.channel-message.render-swipe-action-icon.bookmark-icon" />
			);
		case 'remind':
			return <TimeoutIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.timeout-icon" />;
		case 'mark_unread':
			return (
				<MarkAsUnreadIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.mark-as-unread-icon" />
			);
		case 'copy_message':
			return <CopyTextIcon size={22} data-flx="channel.channel-message.render-swipe-action-icon.copy-text-icon" />;
		case 'open_channel_list':
			return (
				<ArrowLeftIcon
					size={22}
					weight="bold"
					data-flx="channel.channel-message.render-swipe-action-icon.arrow-left-icon"
				/>
			);
		default:
			return null;
	}
};

let mobileLongPressScrollListenerAttached = false;

const handleMobileLongPressWindowScroll = () => {
	for (const handler of mobileLongPressScrollCancelHandlers) {
		handler();
	}
};
const subscribeMobileLongPressScrollCancel = (handler: () => void): (() => void) => {
	mobileLongPressScrollCancelHandlers.add(handler);
	if (!mobileLongPressScrollListenerAttached) {
		window.addEventListener('scroll', handleMobileLongPressWindowScroll, {capture: true, passive: true});
		mobileLongPressScrollListenerAttached = true;
	}
	return () => {
		mobileLongPressScrollCancelHandlers.delete(handler);
		if (mobileLongPressScrollListenerAttached && mobileLongPressScrollCancelHandlers.size === 0) {
			window.removeEventListener('scroll', handleMobileLongPressWindowScroll, {capture: true});
			mobileLongPressScrollListenerAttached = false;
		}
	};
};
const handleAltClickEvent = (event: React.MouseEvent, message: MessageModel) => {
	if (!event.altKey) return;
	ReadStateCommands.markAsUnread(message.channelId, message.id);
};
const handleAltKeyboardEvent = (event: React.KeyboardEvent, message: MessageModel) => {
	if (!event.altKey || !isActivationKey(event.key)) {
		return;
	}
	event.preventDefault();
	ReadStateCommands.markAsUnread(message.channelId, message.id);
};
const getContextMenuLinkUrl = (target: EventTarget | null): string | undefined => {
	if (!(target instanceof HTMLElement)) {
		return undefined;
	}
	const anchor = target.closest('a');
	if (anchor?.href) {
		return anchor.href;
	}
	const mediaTarget = target.closest('[data-message-emoji="true"], [data-message-sticker="true"]');
	if (!mediaTarget) {
		return undefined;
	}
	const imageElement =
		mediaTarget instanceof HTMLImageElement ? mediaTarget : mediaTarget.querySelector<HTMLImageElement>('img');
	if (!imageElement) {
		return undefined;
	}
	const imageUrl = imageElement.currentSrc || imageElement.src;
	return imageUrl || undefined;
};
const getElementFromEventTarget = (target: EventTarget | null): Element | null => {
	if (target instanceof Element) return target;
	if (target instanceof Node) return target.parentElement;
	return null;
};
const isNestedLongPressOwnerTarget = (target: EventTarget | null): boolean =>
	getElementFromEventTarget(target)?.closest(NESTED_LONG_PRESS_OWNER_SELECTOR) != null;
const isDoubleClickInteractiveTarget = (target: EventTarget | null): boolean =>
	getElementFromEventTarget(target)?.closest(DOUBLE_CLICK_INTERACTIVE_TARGET_SELECTOR) != null;
const normalizeAriaLabelText = (text: string): string => text.replace(/\s+/g, ' ').trim();
const truncateAriaLabelText = (text: string): string => {
	const normalized = normalizeAriaLabelText(text);
	if (normalized.length <= MAX_ARIA_MESSAGE_TEXT_LENGTH) {
		return normalized;
	}
	return `${normalized.slice(0, MAX_ARIA_MESSAGE_TEXT_LENGTH - 1).trimEnd()}...`;
};
export type MessageBehaviorOverrides = Partial<{
	mobileLayoutEnabled: boolean;
	messageGroupSpacing: number;
	messageDisplayCompact: boolean;
	isEditing: boolean;
	isReplying: boolean;
	isHighlight: boolean;
	forceUnknownMessageType: boolean;
	contextMenuOpen: boolean;
	disableContextMenu: boolean;
	disableContextMenuTracking: boolean;
	disableDoubleClick: boolean;
}>;

interface MessageProps {
	channel: Channel;
	message: MessageModel;
	prevMessage?: MessageModel;
	onEdit?: (targetNode: HTMLElement) => void;
	previewContext?: keyof typeof MessagePreviewContext;
	shouldGroup?: boolean;
	previewOverrides?: {
		usernameColor?: string;
		displayName?: string;
	};
	removeTopSpacing?: boolean;
	isJumpTarget?: boolean;
	behaviorOverrides?: MessageBehaviorOverrides;
	compact?: boolean;
	idPrefix?: string;
	suppressMessageActions?: boolean;
	onHeadingActivate?: () => void;
	/** Unitless px headroom for avatar costume scrunch (group start only). */
	costumeHeadroomPx?: number | null;
	/** Previous cozy group's message ids, used to add reaction-row headroom. */
	costumeHeadroomReactionMessageIds?: ReadonlyArray<string> | null;
}

export const Message: React.FC<MessageProps> = observer((props) => {
	const {
		channel,
		message,
		prevMessage,
		onEdit,
		previewContext,
		shouldGroup = false,
		previewOverrides,
		removeTopSpacing = false,
		isJumpTarget = false,
		behaviorOverrides,
		compact,
		idPrefix = 'message',
		suppressMessageActions,
		onHeadingActivate,
		costumeHeadroomPx = null,
		costumeHeadroomReactionMessageIds = null,
	} = props;
	const {i18n} = useLingui();
	const location = useLocation();
	const [showActionBar, setShowActionBar] = useState(false);
	const [isLongPressing, setIsLongPressing] = useState(false);
	const [isFocusedWithin, setIsFocusedWithin] = useState(false);
	const [mobileLongPressLinkUrl, setMobileLongPressLinkUrl] = useState<string | undefined>(undefined);
	const [swipeOffset, setSwipeOffset] = useState(0);
	const messageRef = useRef<HTMLDivElement | null>(null);
	const focusRingAnchorRef = useRef<HTMLDivElement | null>(null);
	const disableContextMenuTracking = behaviorOverrides?.disableContextMenuTracking ?? false;
	const trackedContextMenuOpen = useContextMenuHoverState(messageRef, !disableContextMenuTracking);
	const contextMenuOpen = disableContextMenuTracking
		? (behaviorOverrides?.contextMenuOpen ?? false)
		: trackedContextMenuOpen;
	const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
	const unsubscribeLongPressScrollCancelRef = useRef<(() => void) | null>(null);
	const swipeAxisRef = useRef<'none' | 'horizontal' | 'vertical'>('none');
	const swipeOffsetRef = useRef(0);
	const wasEditingInPreviousUpdateRef = useRef(false);
	const mobileLayoutEnabled = behaviorOverrides?.mobileLayoutEnabled ?? MobileLayout.isEnabled();
	const messageDisplayCompact =
		compact ?? behaviorOverrides?.messageDisplayCompact ?? UserSettings.getMessageDisplayCompact();
	const prefersReducedMotion = Accessibility.useReducedMotion;
	const isOwnMessage = message.isCurrentUserAuthor();
	const messageSwipeLeftAction = isOwnMessage
		? Accessibility.messageSwipeOwnLeftAction
		: Accessibility.messageSwipeOthersLeftAction;
	const messageSwipeRightAction = isOwnMessage
		? Accessibility.messageSwipeOwnRightAction
		: Accessibility.messageSwipeOthersRightAction;
	const swipePermissions = useMemo(() => getMessagePermissions(message, channel), [message, channel]);
	const isEditing = behaviorOverrides?.isEditing ?? MessageEdit.isEditing(message.channelId, message.id);
	const isReplying = behaviorOverrides?.isReplying ?? MessageReply.isReplying(message.channelId, message.id);
	const isHighlight = behaviorOverrides?.isHighlight ?? MessageReply.isHighlight(message.id);
	const forceUnknownMessageType =
		behaviorOverrides?.forceUnknownMessageType ?? DeveloperOptions.forceUnknownMessageType;
	const messageGroupSpacing =
		behaviorOverrides?.messageGroupSpacing ?? Accessibility.getMessageGroupSpacingValue(messageDisplayCompact);
	const authorName =
		previewOverrides?.displayName || NicknameUtils.getNickname(message.author, channel.guildId, channel.id);
	const {nodes: astNodes} = useMemo(
		() =>
			parse({
				content: message.content,
				context: MarkdownContext.STANDARD_WITH_JUMBO,
			}),
		[message.content],
	);
	const messageAriaLabel = useMemo(() => {
		const timeLabel = DateUtils.getFormattedDateTime(message.timestamp);
		const systemText = message.isSystemMessage() ? SystemMessageUtils.stringify(message, i18n) : null;
		let text =
			systemText ||
			renderAstToPlaintext(astNodes, {
				channelId: channel.id,
				preserveMarkdown: false,
				includeEmojiNames: true,
				i18n,
			});
		if (!text && message.attachments.length > 0) {
			text =
				message.attachments.length === 1
					? i18n._(ATTACHMENT_DESCRIPTOR)
					: i18n._(ATTACHMENTS_DESCRIPTOR, {length: message.attachments.length});
		}
		if (!text && message.stickerItems.length > 0) {
			text =
				message.stickerItems.length === 1
					? i18n._(STICKER_DESCRIPTOR)
					: i18n._(STICKERS_DESCRIPTOR, {length: message.stickerItems.length});
		}
		if (!text && message.embeds.length > 0) {
			text =
				message.embeds.length === 1
					? i18n._(EMBED_DESCRIPTOR)
					: i18n._(EMBEDS_DESCRIPTOR, {length: message.embeds.length});
		}
		if (!text) {
			text = i18n._(NO_TEXT_CONTENT_DESCRIPTOR);
		}
		return `${authorName}, ${truncateAriaLabelText(text)}, ${timeLabel}, ${i18n._(MESSAGE_DESCRIPTOR)}`;
	}, [
		astNodes,
		authorName,
		channel.id,
		i18n.locale,
		message,
		message.attachments.length,
		message.content,
		message.embeds.length,
		message.stickerItems.length,
		message.timestamp,
	]);
	const handleAltClick = useCallback(
		(event: React.MouseEvent) => {
			handleAltClickEvent(event, message);
		},
		[message],
	);
	const handleDoubleClick = useCallback(
		(event: React.MouseEvent) => {
			if (
				behaviorOverrides?.disableDoubleClick ||
				!Accessibility.messageDoubleClickActions ||
				mobileLayoutEnabled ||
				(previewContext && previewContext !== MessagePreviewContext.LIST_POPOUT) ||
				message.state !== MessageStates.SENT ||
				!message.isUserMessage() ||
				isEditing ||
				isDoubleClickInteractiveTarget(event.target)
			) {
				return;
			}
			const permissions = getMessagePermissions(message, channel);
			if (!permissions?.canSendMessages) return;
			if (permissions.canEditMessage && !message.messageSnapshots) {
				startMessageEdit(message);
				return;
			}
			if (!isReplying) requestMessageReply(message, {sourceChannel: channel});
		},
		[
			previewContext,
			message,
			channel,
			isEditing,
			isReplying,
			mobileLayoutEnabled,
			behaviorOverrides?.disableDoubleClick,
		],
	);
	const handleAltKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			handleAltKeyboardEvent(event, message);
		},
		[message],
	);
	const handleDelete = useCallback(
		(bypassConfirm = false) => {
			requestDeleteMessage(message, i18n, bypassConfirm);
		},
		[i18n, message],
	);
	const handleContextMenu = useCallback(
		(event: React.MouseEvent) => {
			if (behaviorOverrides?.disableContextMenu) {
				event.preventDefault();
				return;
			}
			if (
				(previewContext && previewContext !== MessagePreviewContext.LIST_POPOUT) ||
				message.state === MessageStates.SENDING ||
				isEditing
			) {
				return;
			}
			event.preventDefault();
			if (mobileLayoutEnabled) {
				return;
			}
			MessageFocus.holdContextFocus(channel.id, message.id, message, channel);
			const linkUrl = getContextMenuLinkUrl(event.target);
			ContextMenuCommands.openFromEvent(event, (props) => (
				<MessageContextMenu
					message={message}
					sourceChannel={channel}
					onClose={props.onClose}
					onDelete={handleDelete}
					linkUrl={linkUrl}
					data-flx="channel.message.handle-context-menu.message-context-menu"
				/>
			));
		},
		[
			previewContext,
			message,
			channel,
			isEditing,
			mobileLayoutEnabled,
			handleDelete,
			behaviorOverrides?.disableContextMenu,
		],
	);
	const touchStartPos = useRef<{x: number; y: number} | null>(null);
	const velocitySamples = useRef<Array<VelocitySample>>([]);
	const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);
	const suppressClickUntilRef = useRef(0);
	const unsubscribeLongPressScrollCancel = useCallback(() => {
		unsubscribeLongPressScrollCancelRef.current?.();
		unsubscribeLongPressScrollCancelRef.current = null;
	}, []);
	const clearLongPressState = useCallback(
		(options?: {preserveLinkUrl?: boolean; preserveTouchStart?: boolean}) => {
			unsubscribeLongPressScrollCancel();
			if (longPressTimerRef.current) {
				clearTimeout(longPressTimerRef.current);
				longPressTimerRef.current = null;
			}
			if (highlightTimerRef.current) {
				clearTimeout(highlightTimerRef.current);
				highlightTimerRef.current = null;
			}
			if (!options?.preserveTouchStart) {
				touchStartPos.current = null;
				velocitySamples.current = [];
			}
			setIsLongPressing(false);
			if (!options?.preserveLinkUrl) {
				setMobileLongPressLinkUrl(undefined);
			}
		},
		[unsubscribeLongPressScrollCancel],
	);
	const resetSwipeState = useCallback(() => {
		swipeAxisRef.current = 'none';
		swipeOffsetRef.current = 0;
		setSwipeOffset(0);
	}, []);
	const actionForSwipeOffset = useCallback(
		(offset: number): MessageSwipeActionId => {
			const configured = offset > 0 ? messageSwipeRightAction : offset < 0 ? messageSwipeLeftAction : ('none' as const);
			if (configured === 'none') {
				return 'none';
			}
			// Unavailable actions behave like None: no claim, no reveal, no silent commit no-op.
			if (!isMessageSwipeActionAvailable(configured, message, swipePermissions)) {
				return 'none';
			}
			return configured;
		},
		[message, messageSwipeLeftAction, messageSwipeRightAction, swipePermissions],
	);
	const calculateHorizontalVelocity = useCallback((): number => {
		const samples = velocitySamples.current;
		if (samples.length < 2) return 0;
		const now = performance.now();
		let firstRecentIndex = samples.length - 1;
		for (let i = samples.length - 1; i >= 0; i--) {
			if (now - samples[i].timestamp >= 100) {
				break;
			}
			firstRecentIndex = i;
		}
		if (samples.length - firstRecentIndex < 2) return 0;
		const first = samples[firstRecentIndex];
		const last = samples[samples.length - 1];
		const dt = last.timestamp - first.timestamp;
		if (dt === 0) return 0;
		return (last.x - first.x) / dt;
	}, []);
	const handleLongPressStart = useCallback(
		(event: React.TouchEvent) => {
			if (!mobileLayoutEnabled || previewContext || isNestedLongPressOwnerTarget(event.target) || isEditing) {
				return;
			}
			const touch = event.touches[0];
			if (!touch) return;
			resetSwipeState();
			unsubscribeLongPressScrollCancel();
			setMobileLongPressLinkUrl(getContextMenuLinkUrl(event.target));
			unsubscribeLongPressScrollCancelRef.current = subscribeMobileLongPressScrollCancel(() => {
				if (touchStartPos.current) {
					clearLongPressState();
				}
				if (swipeAxisRef.current !== 'horizontal') {
					resetSwipeState();
				}
			});
			touchStartPos.current = {x: touch.clientX, y: touch.clientY};
			velocitySamples.current = [{x: touch.clientX, y: touch.clientY, timestamp: performance.now()}];
			highlightTimerRef.current = setTimeout(() => {
				if (touchStartPos.current && swipeAxisRef.current !== 'horizontal') {
					setIsLongPressing(true);
				}
				highlightTimerRef.current = null;
			}, HIGHLIGHT_DELAY);
			longPressTimerRef.current = setTimeout(() => {
				if (touchStartPos.current && swipeAxisRef.current !== 'horizontal') {
					suppressClickUntilRef.current = performance.now() + SUPPRESS_POST_LONG_PRESS_CLICK_MS;
					setShowActionBar(true);
					setIsLongPressing(false);
				}
				clearLongPressState({preserveLinkUrl: true});
			}, LONG_PRESS_DELAY);
		},
		[
			mobileLayoutEnabled,
			previewContext,
			isEditing,
			clearLongPressState,
			unsubscribeLongPressScrollCancel,
			resetSwipeState,
		],
	);
	const handleLongPressEnd = useCallback(() => {
		const start = touchStartPos.current;
		const axis = swipeAxisRef.current;
		const offset = swipeOffsetRef.current;
		const velocityX = calculateHorizontalVelocity();
		clearLongPressState();
		if (axis === 'horizontal' && start) {
			const action = actionForSwipeOffset(offset);
			const committed = action !== 'none' && shouldCommitSwipe(offset, velocityX);
			if (committed) {
				const didRun = executeMessageSwipeAction(action, {
					i18n,
					message,
					channel,
					pathname: location.pathname,
					permissions: swipePermissions,
				});
				if (didRun) {
					vibrateMessageSwipeCommit();
					suppressClickUntilRef.current = performance.now() + SUPPRESS_POST_LONG_PRESS_CLICK_MS;
				}
			}
		}
		resetSwipeState();
	}, [
		actionForSwipeOffset,
		calculateHorizontalVelocity,
		channel,
		clearLongPressState,
		i18n,
		location.pathname,
		message,
		resetSwipeState,
		swipePermissions,
	]);
	const handleLongPressMove = useCallback(
		(event: React.TouchEvent) => {
			if (!touchStartPos.current) return;
			const touch = event.touches[0];
			if (!touch) return;
			pushVelocitySample(velocitySamples.current, touch.clientX, touch.clientY);
			const deltaX = touch.clientX - touchStartPos.current.x;
			const deltaY = touch.clientY - touchStartPos.current.y;
			if (swipeAxisRef.current === 'none') {
				const axis = resolveSwipeAxis(deltaX, deltaY);
				if (axis === 'none') {
					return;
				}
				// Direction set to None must not claim the gesture or steal scroll.
				if (axis === 'horizontal' && actionForSwipeOffset(deltaX) === 'none') {
					swipeAxisRef.current = 'vertical';
					clearLongPressState();
					return;
				}
				swipeAxisRef.current = axis;
				if (axis === 'vertical') {
					clearLongPressState();
					return;
				}
				clearLongPressState({preserveLinkUrl: true, preserveTouchStart: true});
			}
			if (swipeAxisRef.current !== 'horizontal') {
				return;
			}
			event.preventDefault();
			let nextOffset = clampSwipeOffset(deltaX);
			const action = actionForSwipeOffset(nextOffset);
			if (action === 'none') {
				nextOffset = 0;
			}
			swipeOffsetRef.current = nextOffset;
			setSwipeOffset(nextOffset);
		},
		[actionForSwipeOffset, clearLongPressState],
	);
	const handleClickCapture = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (mobileLayoutEnabled && performance.now() <= suppressClickUntilRef.current) {
				event.preventDefault();
				event.stopPropagation();
				return;
			}
			// LinkRenderer's <a> always stopPropagation() on bubble click (~919-920), so
			// last-read ack must run in capture or a markdown/external link never advances
			// last-read. Do not preventDefault/stopPropagation here: the click must still
			// reach the <a> so the link opens.
			if (
				!shouldAckMessageOnRowClick({
					enabled: UnreadCatchUpSettings.markReadToMessage,
					isPreview: previewContext != null,
					altKey: event.altKey,
					selectionCollapsed: (() => {
						const selection = event.currentTarget.ownerDocument.defaultView?.getSelection();
						return selection == null || selection.isCollapsed;
					})(),
					messageId: message.id,
					ackMessageId: ReadStates.ackMessageId(message.channelId),
				})
			) {
				return;
			}
			if (isMarkUnreadChromeTarget(event.target)) {
				return;
			}
			ReadStateCommands.advanceLastReadForInteraction(message.channelId, message.id);
		},
		[message, mobileLayoutEnabled, previewContext],
	);
	const handleContextMenuCapture = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (
				!shouldAckMessageOnRowClick({
					enabled: UnreadCatchUpSettings.markReadToMessage,
					isPreview: previewContext != null,
					altKey: event.altKey,
					selectionCollapsed: (() => {
						const selection = event.currentTarget.ownerDocument.defaultView?.getSelection();
						return selection == null || selection.isCollapsed;
					})(),
					messageId: message.id,
					ackMessageId: ReadStates.ackMessageId(message.channelId),
				})
			) {
				return;
			}
			if (isMarkUnreadChromeTarget(event.target)) {
				return;
			}
			ReadStateCommands.advanceLastReadForInteraction(message.channelId, message.id);
		},
		[message, previewContext],
	);
	const handleBottomSheetClose = useCallback(() => {
		setShowActionBar(false);
		setMobileLongPressLinkUrl(undefined);
	}, []);
	const keyboardModeEnabled = KeyboardMode.keyboardModeEnabled;
	const {isHovering, isPopoutOpen, handlePopoutToggle} = useMessageHoverState({
		messageRef,
		mobileLayoutEnabled,
		contextMenuOpen,
	});
	const handleFocusWithin = useCallback(() => {
		if (!keyboardModeEnabled) {
			return;
		}
		setIsFocusedWithin(true);
		MessageFocus.focusMessage(channel.id, message.id, message, channel);
	}, [channel, message, keyboardModeEnabled]);
	const handleBlurWithin = useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			const nextTarget = event.relatedTarget;
			if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
				return;
			}
			setIsFocusedWithin(false);
			MessageFocus.blurMessage(channel.id, message.id);
		},
		[channel.id, message.id],
	);
	useEffect(() => {
		if (!keyboardModeEnabled) return;
		if (contextMenuOpen) {
			MessageFocus.holdContextFocus(channel.id, message.id, message, channel);
			return;
		}
		MessageFocus.releaseContextFocus(channel.id, message.id);
		if (!isFocusedWithin) {
			MessageFocus.clearFocusedMessageIfMatches(channel.id, message.id);
		}
	}, [channel, contextMenuOpen, isFocusedWithin, keyboardModeEnabled, message, message.id]);
	const isFocusedWithinRef = useRef(isFocusedWithin);
	isFocusedWithinRef.current = isFocusedWithin;
	useEffect(() => {
		return () => {
			if (isFocusedWithinRef.current) {
				MessageFocus.clearFocusedMessageIfMatches(channel.id, message.id);
			}
		};
	}, [channel.id, message.id]);
	useEffect(() => {
		const wasEditing = wasEditingInPreviousUpdateRef.current;
		const justStartedEditing = !wasEditing && isEditing;
		if (justStartedEditing && onEdit && messageRef.current) {
			onEdit(messageRef.current);
		}
		wasEditingInPreviousUpdateRef.current = isEditing;
	}, [isEditing, onEdit]);
	useEffect(() => {
		return () => {
			unsubscribeLongPressScrollCancel();
			if (longPressTimerRef.current) {
				clearTimeout(longPressTimerRef.current);
			}
			if (highlightTimerRef.current) {
				clearTimeout(highlightTimerRef.current);
			}
		};
	}, [unsubscribeLongPressScrollCancel]);
	useEffect(() => {
		if (!keyboardModeEnabled) {
			setIsFocusedWithin(false);
			return;
		}
		const activeElement = messageRef.current?.ownerDocument?.activeElement ?? document.activeElement;
		if (messageRef.current && activeElement && messageRef.current.contains(activeElement)) {
			setIsFocusedWithin(true);
		}
	}, [keyboardModeEnabled]);
	const messageContextValue = useMemo(
		() => ({
			channel,
			message,
			handleDelete,
			shouldGroup,
			isHovering,
			messageDisplayCompact,
			previewContext,
			previewOverrides,
			onPopoutToggle: handlePopoutToggle,
			suppressMessageActions,
			onHeadingActivate,
			costumeHeadroomPx,
			costumeHeadroomReactionMessageIds,
		}),
		[
			channel,
			message,
			handleDelete,
			shouldGroup,
			isHovering,
			messageDisplayCompact,
			previewContext,
			previewOverrides,
			handlePopoutToggle,
			suppressMessageActions,
			onHeadingActivate,
			costumeHeadroomPx,
			costumeHeadroomReactionMessageIds,
		],
	);
	const messageComponent = (
		<MessageViewContextProvider value={messageContextValue} data-flx="channel.message.message-view-context-provider">
			{getMessageComponent(message, channel, forceUnknownMessageType)}
		</MessageViewContextProvider>
	);
	const shouldHideContent =
		UserSettings.renderEmbeds &&
		message.embeds.length > 0 &&
		message.embeds.every(isMediaOnlyEmbed) &&
		astNodes.length === 1 &&
		astNodes[0].type === NodeType.Link &&
		!message.suppressEmbeds;
	const isKeyboardFocused = keyboardModeEnabled && isFocusedWithin;
	const isPreview = previewContext != null;
	const shouldApplySpacing = !shouldGroup && !removeTopSpacing && previewContext !== MessagePreviewContext.LIST_POPOUT;
	const systemFollowsSystem = Boolean(
		shouldGroup && prevMessage && isDisplaySystemMessage(prevMessage) && isDisplaySystemMessage(message),
	);
	const hasPendingReminder = !previewContext && Reminders.hasPendingReminder(message.id);
	const messageClasses = useMemo(
		() =>
			clsx(
				messageDisplayCompact ? styles.messageCompact : styles.message,
				isHovering && !isPreview && styles.messageHovered,
				isEditing && styles.messageEditing,
				!messageDisplayCompact &&
					shouldGroup &&
					shouldApplyGroupedLayout(message, prevMessage) &&
					styles.messageGrouped,
				systemFollowsSystem && styles.systemMessageFollowsSystem,
				!previewContext && message.isMentioned() && styles.messageMentioned,
				!previewContext && hasPendingReminder && styles.messageReminded,
				!previewContext &&
					(isReplying || isHighlight || isJumpTarget) &&
					(isReplying ? styles.messageReplying : styles.messageHighlight),
				message.type === MessageTypes.CLIENT_SYSTEM && message.author.id === FLUXERBOT_ID && styles.messageClientSystem,
				isLongPressing && styles.messageLongPress,
				!previewContext && (contextMenuOpen || isPopoutOpen) && styles.contextMenuActive,
				previewContext && styles.messagePreview,
				mobileLayoutEnabled && styles.mobileLayout,
				!messageDisplayCompact &&
					(!message.content || shouldHideContent) &&
					!isEditing &&
					message.isUserMessage() &&
					styles.messageNoText,
				isKeyboardFocused && !isPreview && styles.keyboardFocused,
				isKeyboardFocused && !isPreview && 'keyboard-focus-active',
				shouldApplySpacing && previewContext && styles.messagePreviewSpacing,
			),
		[
			messageDisplayCompact,
			isHovering,
			isEditing,
			systemFollowsSystem,
			shouldGroup,
			message,
			prevMessage,
			previewContext,
			hasPendingReminder,
			isReplying,
			isHighlight,
			isJumpTarget,
			isLongPressing,
			contextMenuOpen,
			isPopoutOpen,
			mobileLayoutEnabled,
			shouldHideContent,
			isKeyboardFocused,
			shouldApplySpacing,
			isPreview,
		],
	);
	const shouldShowActionBar = useMemo(
		() =>
			!previewContext &&
			!suppressMessageActions &&
			message.state !== MessageStates.SENDING &&
			!isEditing &&
			!mobileLayoutEnabled,
		[previewContext, suppressMessageActions, message.state, isEditing, mobileLayoutEnabled],
	);
	const isActionBarForcedVisible = useMemo(
		() => Boolean(isKeyboardFocused || contextMenuOpen || isPopoutOpen),
		[isKeyboardFocused, contextMenuOpen, isPopoutOpen],
	);
	const isActionBarActive = isHovering || isActionBarForcedVisible;
	const wasActionBarActivatedRef = useRef(false);
	wasActionBarActivatedRef.current = wasActionBarActivatedRef.current || isActionBarActive;
	const shouldMountActionBar = shouldShowActionBar && wasActionBarActivatedRef.current;
	const shouldShowBottomSheet = useMemo(
		() =>
			mobileLayoutEnabled && showActionBar && !previewContext && message.state !== MessageStates.SENDING && !isEditing,
		[mobileLayoutEnabled, showActionBar, previewContext, message.state, isEditing],
	);
	const activeSwipeAction = actionForSwipeOffset(swipeOffset);
	const articleStyle = useMemo<React.CSSProperties>(
		() => ({
			touchAction: swipeOffset !== 0 ? 'none' : 'pan-y',
			WebkitUserSelect: 'text',
			userSelect: 'text',
			marginTop: shouldApplySpacing && previewContext ? `${messageGroupSpacing}px` : undefined,
			transform: swipeOffset !== 0 ? `translate3d(${swipeOffset}px, 0, 0)` : undefined,
			transition: swipeOffset === 0 && !prefersReducedMotion ? 'transform 160ms ease-out' : undefined,
		}),
		[shouldApplySpacing, previewContext, messageGroupSpacing, swipeOffset, prefersReducedMotion],
	);
	return (
		<>
			<div
				className={clsx(styles.swipeHost, swipeOffset !== 0 && styles.swipeHostClipping)}
				data-flx="channel.message.swipe-host"
			>
				{activeSwipeAction !== 'none' && swipeOffset !== 0 ? (
					<div
						className={clsx(styles.swipeReveal, swipeOffset > 0 ? styles.swipeRevealLeft : styles.swipeRevealRight)}
						style={{
							...getMessageSwipeRevealStyle(activeSwipeAction),
							width: Math.min(Math.abs(swipeOffset), SWIPE_MAX_REVEAL_PX),
						}}
						aria-hidden
						data-flx="channel.message.swipe-reveal"
					>
						{renderSwipeActionIcon(activeSwipeAction)}
					</div>
				) : null}
				<FocusRing
					enabled={keyboardModeEnabled}
					offset={-2}
					ringTarget={focusRingAnchorRef}
					data-flx="channel.message.focus-ring"
				>
					<div
						role="article"
						aria-label={messageAriaLabel}
						id={`${idPrefix}-${channel.id}-${message.id}`}
						data-message-id={message.id}
						data-channel-id={channel.id}
						data-flx-message-id={message.id}
						data-flx-message-type={MESSAGE_TYPE_NAMES[message.type] ?? String(message.type)}
						data-flx-channel-id={channel.id}
						data-flx-guild-id={channel.guildId ?? undefined}
						data-flx-author-id={message.author.id}
						data-flx-author-username={NicknameUtils.formatNameForStreamerMode(message.author.username)}
						data-flx-author-name={authorName}
						data-flx-author-self={message.author.id === Users.currentUserId ? 'true' : undefined}
						data-flx-author-bot={message.author.bot ? 'true' : undefined}
						data-flx-author-webhook={message.webhookId != null ? 'true' : undefined}
						data-flx-reply={
							message.type === MessageTypes.REPLY || (message.messageReference != null && !message.isCrosspostCopy)
								? 'true'
								: undefined
						}
						data-flx-blocked={message.blocked ? 'true' : undefined}
						data-flx-pinned={message.pinned ? 'true' : undefined}
						data-flx-call={message.type === MessageTypes.CALL ? 'true' : undefined}
						data-flx-system={message.isSystemMessage() ? 'true' : undefined}
						data-flx-mentioned={message.isMentioned() ? 'true' : undefined}
						data-flx-edited={message.editedTimestamp != null ? 'true' : undefined}
						data-flx-compact={messageDisplayCompact ? 'true' : undefined}
						data-flx-grouped={shouldGroup && shouldApplyGroupedLayout(message, prevMessage) ? 'true' : undefined}
						data-flx-action-bar={shouldShowActionBar ? 'true' : undefined}
						data-flx-action-bar-active={shouldShowActionBar && isActionBarActive ? 'true' : undefined}
						data-flx-action-bar-forced={shouldShowActionBar && isActionBarForcedVisible ? 'true' : undefined}
						tabIndex={keyboardModeEnabled ? -1 : undefined}
						className={messageClasses}
						ref={messageRef}
						onClickCapture={handleClickCapture}
						onContextMenuCapture={handleContextMenuCapture}
						onClick={handleAltClick}
						onDoubleClick={handleDoubleClick}
						onKeyDown={handleAltKeyDown}
						onFocus={handleFocusWithin}
						onBlur={handleBlurWithin}
						onContextMenu={handleContextMenu}
						onTouchStart={handleLongPressStart}
						onTouchEnd={handleLongPressEnd}
						onTouchMove={handleLongPressMove}
						onTouchCancel={handleLongPressEnd}
						style={articleStyle}
						data-flx="channel.message.article.alt-click"
					>
						<div
							ref={focusRingAnchorRef}
							aria-hidden={true}
							className={styles.focusRingAnchor}
							data-flx="channel.message.focus-ring-anchor"
						/>
						{messageComponent}
						{shouldMountActionBar && (
							<MessageActionBar
								message={message}
								handleDelete={handleDelete}
								sourceChannel={channel}
								isActive={isActionBarActive}
								onPopoutToggle={handlePopoutToggle}
								data-flx="channel.message.message-action-bar"
							/>
						)}
					</div>
				</FocusRing>
			</div>

			{shouldShowBottomSheet && (
				<MessageActionBottomSheet
					isOpen={shouldShowBottomSheet}
					onClose={handleBottomSheetClose}
					message={message}
					sourceChannel={channel}
					handleDelete={handleDelete}
					linkUrl={mobileLongPressLinkUrl}
					data-flx="channel.message.message-action-bottom-sheet"
				/>
			)}
		</>
	);
});
